import type { SkillSourceFailureDto } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useEffect, useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiClientError } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { CARD_CODES, sourceErrorView } from './source-error.js';

/** the failure behind an install error, when the API gave one */
export function sourceFailureOf(error: unknown): SkillSourceFailureDto | null {
  return error instanceof ApiClientError && error.detail ? error.detail : null;
}

/**
 * Phase 16c.3 (SkillGitHubHatalar.dc.html, SkillGitHubCokBuyuk.dc.html): the
 * card that stands where the preview would be. "Dosyadan yükle" opens the
 * Dosyadan tab; "Yeniden dene" looks again; GitHub's request limit counts
 * down on a disabled button until its reset. "Teknik ayrıntılar" has the
 * step, the address, the answer, the time and the error number with
 * "Kopyala" — the number Yönetim › Denetim and the server's log carry. Any
 * other error is the usual alert, with the error number when there is one.
 */
export function SkillSourceError({ error, onUpload, onRetry, retrying }: { error: unknown; onUpload: () => void; onRetry: () => void; retrying?: boolean }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const titleId = useId();
  const detailsId = useId();
  const detail = sourceFailureOf(error);
  const code = error instanceof ApiClientError ? error.code : null;
  const card = Boolean(detail && code && CARD_CODES.has(code));
  // the request limit's countdown, and its button turning into "Yeniden dene"
  useEffect(() => {
    if (!card || code !== 'skill_source_rate_limited') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [card, code]);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2_000);
    return () => clearTimeout(timer);
  }, [copied]);
  if (!error) return null;
  if (!card || !detail || !code) return <ErrorAlert error={error} />;
  const view = sourceErrorView(code, detail, t, i18n.language, zone, now);
  const copy = () => {
    void navigator.clipboard?.writeText(detail.errorId).then(
      () => setCopied(true),
      () => undefined,
    );
  };
  return (
    <section className="reply-failure skill-source-error" role="alert" aria-labelledby={titleId} data-testid={`skill-error-${code}`}>
      <span className="reply-failure__icon" aria-hidden="true">
        <Icons.error size={18} />
      </span>
      <div className="reply-failure__body">
        <h3 id={titleId} className="reply-failure__title">
          {t(view.title.key, view.title.values)}
        </h3>
        {view.body.map((text) => (
          <p key={text.key} className="reply-failure__text">
            <Trans i18nKey={text.key} values={text.values} components={{ code: <code className="skill-source-error__code" /> }} />
          </p>
        ))}
        <div className="reply-failure__actions">
          {view.actions.map((action) =>
            action.kind === 'wait' ? (
              <Button key="wait" className="reply-failure__action" variant="secondary" disabled icon={<Icons.time size={15} />} data-testid="skill-error-wait">
                {t('skills.sourceError.wait', { minutes: action.minutes })}
              </Button>
            ) : action.kind === 'upload' ? (
              <Button key="upload" className="reply-failure__action" variant={action.primary ? 'primary' : 'secondary'} icon={<Icons.upload size={15} />} onClick={onUpload} data-testid="skill-error-upload">
                {t('skills.sourceError.upload')}
              </Button>
            ) : (
              <Button key="retry" className="reply-failure__action" variant={action.primary ? 'primary' : 'secondary'} icon={<Icons.regenerate size={15} />} busy={retrying} onClick={onRetry} data-testid="skill-error-retry">
                {t('skills.sourceError.retry')}
              </Button>
            ),
          )}
          <span className="reply-failure__spacer" />
          <button type="button" className="reply-failure__toggle" aria-expanded={open} aria-controls={open ? detailsId : undefined} onClick={() => setOpen(!open)} data-testid="skill-error-details-toggle">
            {t('skills.sourceError.details')}
            {open ? <Icons.chevronUp size={12} /> : <Icons.chevronDown size={12} />}
          </button>
        </div>
        {open ? (
          <dl id={detailsId} className="reply-failure__details" data-testid="skill-error-details">
            {view.rows.map((row) => (
              <div key={row.key} className="reply-failure__row" data-row={row.key}>
                <dt>{row.label}</dt>
                <dd>
                  {row.mono ? <code className="reply-failure__run">{row.value}</code> : row.value}
                  {row.key === 'error' ? (
                    <button type="button" className="reply-failure__copy" onClick={copy} data-testid="skill-error-copy">
                      {copied ? <Icons.check size={12} /> : <Icons.copy size={12} />}
                      <span aria-live="polite">{copied ? t('skills.sourceError.copied') : t('skills.sourceError.copy')}</span>
                    </button>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </section>
  );
}
